import { useCallback, useState } from 'react'
import { useUser } from '../context/UserContext';
import { getGeocodingForPlaceId, getGoogleMapsShortlinkLocation, searchPlaces } from '../utils/api';
import { PlaceCandidate } from '../models/shared/PlaceCandidate';
import { AdvancedButton } from './AdvancedButton';
import { Place, PlaceGeocoding } from '../models/shared/Place';
import SearchInput from './SearchInput';
import { geocodingToPlace, getCurrentLocation } from '../utils/geolocation';
import { parseSweref99TMCoordinates } from '../utils/sweref99';
import { useText } from '../context/TextContext';
import { toast } from 'react-toastify';

interface Props {
    className?: string;
    place?: Place;
    placeHolder?: string;
    readOnly?: boolean;
    noPointOnMap?: boolean;
    onPlaceChange: (place: Place) => Promise<void>;
}

const parseCoordinates = (query: string): PlaceGeocoding | null => {
    console.log('Parsing coordinates from query:', query);
    const match = query.match(/(-?\d+\.\d+),?\s*(\+?-?\d+\.\d+)/);
    console.log('Regex match result:', match);
    if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        return { lat, lng };
    }
    return null;
}

const parseCoordinatesFromUrl = (query: string): PlaceGeocoding | null => {
    try {
        const url = new URL(query);
        const pathParts = url.pathname.split('/').filter(part => part.length > 0);

        const searchIx = pathParts.findIndex(part => part.toLowerCase() === 'search');
        if (searchIx === -1 || searchIx === pathParts.length - 1) {
            return null;
        }

        const decodedSearchQuery = decodeURIComponent(pathParts[searchIx + 1]);
        return parseCoordinates(decodedSearchQuery);
    } catch (error) {
        return null;
    }
}

const parsePlaceNameFromUrl = (query: string): string | undefined => {
    try {
        const url = new URL(query);
        const pathParts = url.pathname.split('/').filter(part => part.length > 0);

        const placeIx = pathParts.findIndex(part => part.toLowerCase() === 'place');
        if (placeIx === -1 || placeIx === pathParts.length - 1) {
            return
        }
        const decodedPlaceName = decodeURIComponent(pathParts[placeIx + 1]);
        return decodedPlaceName;
    } catch (error) {
        return;
    }
}

const followRedirectUrl = async (url: string): Promise<string | undefined> => {
    const isGoogleMapsShortUrl = url.match(/^https?:\/\/.*goo\.gl\//);
    if (!isGoogleMapsShortUrl) {
        return;
    }
    try {
        return getGoogleMapsShortlinkLocation(url);
    } catch (error) {
        return;
    }
}

function AddressSearch(props: Props) {
    const user = useUser();
    const text = useText();
    const [forceSearch, setForceSearch] = useState(false);
    const [gettingLocation, setGettingLocation] = useState(false);

    const handleSearch = useCallback(async (query: string): Promise<PlaceCandidate[]> => {
        let finalQuery = query.trim();

        const redirectedUrl = await followRedirectUrl(finalQuery);
        if (redirectedUrl) {
            finalQuery = redirectedUrl;
            console.log('Following redirect to:', finalQuery);
        }

        const queryFromUrl = parsePlaceNameFromUrl(finalQuery);
        let parsedCoords;
        if (queryFromUrl) {
            finalQuery = queryFromUrl;
            parsedCoords = parseCoordinates(finalQuery) || parseSweref99TMCoordinates(finalQuery);
        } else {
            parsedCoords = parseCoordinatesFromUrl(finalQuery) || parseCoordinates(finalQuery) || parseSweref99TMCoordinates(finalQuery);
        }

        if (parsedCoords) {
            const place = geocodingToPlace(parsedCoords);
            return [{
                placeId: place.placeId,
                formattedAddress: place.formattedAddress,
                displayName: place.name,
                place
            }];
        }

        const token = await user.getToken();
        const suggestions = await searchPlaces(token, finalQuery);
        return suggestions;
    }, [user]);

    const handleUsePointSelect = async () => {
        setGettingLocation(true);
        let location: PlaceGeocoding = { lat: 58.589205, lng: 16.185438 };
        try {
            location = await getCurrentLocation();
        } catch (error: any) {
            console.warn('Error getting current location:', error);
        }

        const place = geocodingToPlace(location);
        await props.onPlaceChange(place);
        setGettingLocation(false);
    }

    const handleUseCurrentLocation = async () => {
        setGettingLocation(true);
        try {
            const location = await getCurrentLocation();
            const place = geocodingToPlace(location);

            await props.onPlaceChange(place);
            setForceSearch(false);
        } catch (error: any) {
            console.error('Error getting current location:', error);
            toast.error(error.message || text.locationError);
        }
        setGettingLocation(false);
    };


    const handleSelect = async (candidate: PlaceCandidate) => {
        if (candidate.place) {
            await props.onPlaceChange(candidate.place);
        } else {
            const token = await user.getToken();
            const geocoding = await getGeocodingForPlaceId(token, candidate.placeId);

            const newPlace: Place = {
                placeId: candidate.placeId,
                formattedAddress: candidate.formattedAddress,
                name: candidate.displayName,
                geocoding
            }
            await props.onPlaceChange(newPlace);
        }

        setForceSearch(false);
    }


    if (props.place && !forceSearch) {
        return <div className='d-flex justify-content-between align-items-start'>
            <div>
                <p className='m-0'>{props.place.name}</p>
                <p className='m-0 text-muted small'>{props.place.formattedAddress}</p>
            </div>
            {!props.readOnly && <AdvancedButton variant='clear' className='p-0' onClick={() => setForceSearch(true)} icon='bi-pencil' size='sm' />}
        </div>
    }

    if (props.readOnly) {
        return null;
    }

    return <>
        <SearchInput
            searchCallback={handleSearch}
            renderCandidate={(candidate) => <div>
                <strong>{candidate.displayName}</strong>
                <div>{candidate.formattedAddress}</div>
            </div>}
            extraAction={<>
                {!props.noPointOnMap && <AdvancedButton
                    variant='outline-primary'
                    size='sm'
                    onClick={handleUsePointSelect}
                    processing={gettingLocation}
                    icon={'bi-map'}
                    title={text.pickOnMap}
                />
                }
                <AdvancedButton
                    variant='outline-primary'
                    size='sm'
                    onClick={handleUseCurrentLocation}
                    processing={gettingLocation}
                    icon={'bi-crosshair'}
                    title={gettingLocation ? text.gettingLocation : text.useCurrentLocation}
                />
            </>
            }
            cancelable={forceSearch}
            onCancel={() => setForceSearch(false)}
            className={props.className}
            placeHolder={props.placeHolder}
            handleSelect={handleSelect}
        />

    </>
}

export default AddressSearch
